import { useCallback, useState } from "react";
import { EYEBROW_CLASS, NOTE_CLASS } from "../lib/ui.js";
import { SourceLogo, StatusPill, secondaryBtnCls, toneDotClass } from "./statusCard.jsx";

export const inputCls =
    "min-h-[32px] w-full rounded-[2px] border border-[color:var(--line-strong)] bg-transparent px-[10px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-soft)] focus:border-[color:var(--ink)]";

export const linkCls =
    "cursor-pointer border-0 bg-transparent p-0 text-[12px] text-[color:var(--ink)] underline underline-offset-2 hover:text-[color:var(--green)]";

const STEP_BADGE = {
    done: "bg-[color:var(--green)] text-white",
    current: "bg-[color:var(--ink)] text-[color:var(--bg)]",
    failed: "bg-[color:var(--severity-high)] text-white",
    todo: "border border-[color:var(--line-strong)] text-[color:var(--muted)]",
};

const STEP_STATE_TEXT = {
    done: "Done",
    current: "Next step",
    failed: "Needs attention",
    todo: "Not started",
};

export function Steps({ children, label }) {
    return (
        <ol aria-label={label} className="m-0 grid list-none gap-0 p-0">
            {children}
        </ol>
    );
}

// One numbered setup step. `summary` is the one-line current value shown in
// every state; `children` is the working area (form, list, checklist).
export function Step({ number, state, title, summary, action, children, last = false }) {
    return (
        <li
            aria-current={state === "current" ? "step" : undefined}
            className="grid grid-cols-[28px_minmax(0,1fr)] gap-x-[14px]"
        >
            <div className="flex flex-col items-center">
                <span
                    className={`inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${STEP_BADGE[state] ?? STEP_BADGE.todo}`}
                >
                    {state === "done" ? (
                        <svg aria-hidden="true" viewBox="0 0 12 12" className="h-[12px] w-[12px]">
                            <path d="M2.5 6.5 5 9l4.5-6" fill="none" stroke="currentColor" strokeWidth="1.8" />
                        </svg>
                    ) : (
                        <span aria-hidden="true">{state === "failed" ? "!" : number}</span>
                    )}
                    <span className="sr-only">{`Step ${number}: ${STEP_STATE_TEXT[state] ?? ""}`}</span>
                </span>
                {last ? null : <span aria-hidden="true" className="mt-[4px] w-px flex-1 bg-[color:var(--line-strong)]" />}
            </div>
            <div className={`grid min-w-0 gap-[10px] pt-[3px] ${last ? "" : "pb-[22px]"}`}>
                <div className="flex flex-wrap items-start justify-between gap-[10px]">
                    <div className="grid min-w-0 gap-[3px]">
                        <h3
                            className={`m-0 text-[14px] font-medium leading-[1.25] ${state === "todo" && !children ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}
                        >
                            {title}
                        </h3>
                        {summary ? (
                            <div
                                className={`text-[12px] leading-[1.4] ${state === "failed" ? "text-[color:var(--severity-high)]" : "text-[color:var(--muted)]"}`}
                            >
                                {summary}
                            </div>
                        ) : null}
                    </div>
                    {action ? <div className="flex items-center gap-[8px]">{action}</div> : null}
                </div>
                {children}
            </div>
        </li>
    );
}

export function CopyValue({ label, value }) {
    const [copied, setCopied] = useState(false);
    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {}
    }, [value]);
    return (
        <div className="flex gap-[8px]">
            <input
                aria-label={label}
                type="text"
                readOnly
                value={value}
                className={`${inputCls} min-w-0 flex-1 font-mono`}
            />
            <button type="button" onClick={copy} className={secondaryBtnCls}>
                {copied ? "Copied" : "Copy"}
            </button>
        </div>
    );
}

export function Field({ label, hint, error = null, children }) {
    return (
        <label className="grid min-w-0 gap-[5px]">
            <span className="text-[12px] font-medium text-[color:var(--ink)]">{label}</span>
            {children}
            {error ? (
                <span className="text-[11px] leading-[1.4] text-[color:var(--severity-high)]">{error}</span>
            ) : hint ? (
                <span className={NOTE_CLASS}>{hint}</span>
            ) : null}
        </label>
    );
}

export function MethodOption({ name, selected, title, description, onSelect, disabledReason = null }) {
    const disabled = Boolean(disabledReason);
    return (
        <label
            title={disabledReason ?? undefined}
            className={`flex items-start gap-[10px] border-t border-[color:var(--line)] px-[12px] py-[10px] first:border-t-0 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${selected ? "bg-[color:var(--gray-fill)]" : ""}`}
        >
            <input
                type="radio"
                name={name}
                checked={selected}
                onChange={onSelect}
                disabled={disabled}
                className="mt-[3px]"
            />
            <span className="grid gap-[2px]">
                <span className="text-[12px] font-medium text-[color:var(--ink)]">{title}</span>
                <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">{disabledReason ?? description}</span>
            </span>
        </label>
    );
}

export function PermissionList({ title, groups, note = null }) {
    return (
        <details className="border border-[color:var(--line)] bg-[color:var(--panel)] px-[14px] py-[10px]">
            <summary className="cursor-pointer text-[12px] font-medium text-[color:var(--ink)]">{title}</summary>
            <div className="mt-[10px] grid gap-[10px]">
                {groups.map((group, index) => (
                    <div key={group.caption ?? index} className="grid gap-[4px]">
                        {group.caption ? <span className={NOTE_CLASS}>{group.caption}</span> : null}
                        <ul className="m-0 grid list-none gap-[3px] p-0">
                            {group.permissions.map((permission) => (
                                <li key={permission.scope} className="flex flex-wrap items-baseline gap-x-[8px]">
                                    <code className="font-mono text-[12px] text-[color:var(--ink)]">{permission.scope}</code>
                                    <span className="text-[11px] text-[color:var(--muted)]">{permission.note}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
                {note ? <p className="m-0 text-[11px] leading-[1.45] text-[color:var(--muted)]">{note}</p> : null}
            </div>
        </details>
    );
}

export function ConsentWait({ onCancel }) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-[10px] border border-[color:var(--line)] px-[12px] py-[10px]">
            <span className="text-[12px] text-[color:var(--ink)]">Waiting for admin consent in the new tab...</span>
            <button type="button" onClick={onCancel} className={secondaryBtnCls}>
                Cancel
            </button>
        </div>
    );
}

export function Instructions({ children }) {
    return (
        <ol className="m-0 grid list-decimal gap-[8px] pl-[18px] text-[12px] leading-[1.5] text-[color:var(--ink)] marker:text-[color:var(--muted)]">
            {children}
        </ol>
    );
}

export function Panel({ children, className = "" }) {
    return (
        <div className={`border border-[color:var(--line)] bg-[color:var(--panel)] ${className}`}>{children}</div>
    );
}

export function AsideSection({ title, children }) {
    return (
        <div className="grid gap-[8px] border-t border-[color:var(--line)] pt-[14px] first:border-t-0 first:pt-0">
            <span className={EYEBROW_CLASS}>{title}</span>
            <div className="grid gap-[8px] text-[12px] leading-[1.5] text-[color:var(--ink)]">{children}</div>
        </div>
    );
}

export function ErrorText({ children }) {
    if (!children) return null;
    return (
        <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[color:var(--severity-high)]">
            {children}
        </p>
    );
}

// A source page whose data has not loaded: still loading, or the load failed
// and can be retried in place.
export function SourceLoadState({ entry, onRetry }) {
    if (!entry?.loaded || entry.loading) {
        return <p className="m-0 text-[12px] text-[color:var(--muted)]">Loading...</p>;
    }
    return (
        <div className="grid justify-items-start gap-[10px]">
            <ErrorText>{entry.error || "Could not load this source."}</ErrorText>
            <button type="button" onClick={onRetry} className={secondaryBtnCls}>
                Retry
            </button>
        </div>
    );
}

// The frame every source page shares: back link, title and status, optional
// tabs and the steps in a capped column, beside an aside of what the
// connection does that starts level with the back link.
export function SourcePage({ source, health, onBack, backLabel, tabs = null, aside, children, busy = false }) {
    return (
        <div className="grid items-start gap-[28px] lg:grid-cols-[minmax(0,680px)_300px] lg:justify-between">
            <div className="grid min-w-0 gap-[18px]">
                <div className="grid gap-[8px]">
                    <button type="button" onClick={onBack} className={`${secondaryBtnCls} w-fit gap-[6px] pl-[8px]`}>
                        <svg
                            viewBox="0 0 16 16"
                            aria-hidden="true"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-[12px] w-[12px]"
                        >
                            <path d="M10 3.5 5.5 8 10 12.5" />
                        </svg>
                        {backLabel}
                    </button>
                    <div className="flex flex-wrap items-center gap-[12px]">
                        <SourceLogo logo={source.logo} size={26} />
                        <h2 className="m-0 text-[22px] font-medium leading-[1.1] tracking-[-0.02em] text-[color:var(--ink)]">
                            {source.name}
                        </h2>
                        {health ? <StatusPill tone={health.tone} label={health.label} /> : null}
                    </div>
                    <p className="m-0 text-[13px] leading-[1.45] text-[color:var(--muted)]">{source.blurb}</p>
                </div>
                {tabs}
                <div aria-busy={busy ? "true" : undefined} className="min-w-0">
                    {children}
                </div>
            </div>
            {aside ? (
                <aside className="grid gap-[14px] border border-[color:var(--line)] bg-[color:var(--panel)] p-[16px]">
                    {aside}
                </aside>
            ) : null}
        </div>
    );
}

export function Tabs({ label, items, selected, onSelect }) {
    return (
        <div role="tablist" aria-label={label} className="flex flex-wrap gap-x-[22px] border-b border-[color:var(--line)]">
            {items.map((item) => {
                const active = item.id === selected;
                return (
                    <button
                        key={item.id}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onSelect(item.id)}
                        className={[
                            "-mb-px flex cursor-pointer items-center gap-[8px] border-0 border-b-2 bg-transparent px-[2px] py-[9px] text-[12px]",
                            item.mono ? "font-mono" : "",
                            active
                                ? "border-[color:var(--ink)] font-medium text-[color:var(--ink)]"
                                : "border-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]",
                        ].join(" ")}
                    >
                        {item.dotTone ? (
                            <span
                                aria-hidden="true"
                                className={`h-[7px] w-[7px] rounded-full ${toneDotClass(item.dotTone)}`}
                            />
                        ) : null}
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
}
